import React from "react";

import type { UseFormReturn } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { countries } from "@/utils/countries";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { parseJSONField } from "@/utils/helper/parseJSON";
import { Badge } from "@/components/ui/badge";
import { Building, Mail, MapPin, Phone, User } from "lucide-react";

type ShippingInformationProps = {
  form: UseFormReturn<any>;
  existingShippingAddress: any;
  saveShippingAddress: (value: boolean) => void;
  onShippingAddressNameSave: (name: string) => void;
  onExistingAddressSelection?: (addressName: string) => void; // Add this
};

const ShippingInformation = ({
  form,
  existingShippingAddress,
  saveShippingAddress,
  onShippingAddressNameSave,
  onExistingAddressSelection, // Add this
}: ShippingInformationProps) => {
  const [wantToSaveAddress, setWantToSaveAddress] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [addressName, setAddressName] = useState("");

  const handleSaveShippingAddress = (checked: boolean) => {
    setWantToSaveAddress(checked);
    if (checked) {
      setShowNameDialog(true);
    } else {
      saveShippingAddress(false);
    }
  };

  const handleAddressNameSave = () => {
    if (!addressName.trim()) {
      toast.error("Please enter a name for the address");
      return;
    }

    // Check if address name already exists
    if (existingShippingAddress && existingShippingAddress.length > 0) {
      const nameExists = existingShippingAddress.some((addr: any) => {
        try {
          const parsedAddr = parseJSONField(addr);
          return parsedAddr?.name?.toLowerCase() === addressName.trim().toLowerCase();
        } catch {
          return false;
        }
      });

      if (nameExists) {
        toast.error("You already have a saved address with this name", {
          description: "Please choose a different name",
        });
        return;
      }
    }

    // Save the address with the name
    onShippingAddressNameSave(addressName.trim());
    saveShippingAddress(true);
    setShowNameDialog(false);
    toast.success("Address will be saved for future use");
  };

  const handleDialogClose = () => {
    setShowNameDialog(false);
    setWantToSaveAddress(false);
    setAddressName("");
  };

  // Handle address selection and auto-fill form
  const handleAddressSelection = (selectedAddressName: string) => {
    if (!selectedAddressName) return;

    // Call the parent callback first
    if (onExistingAddressSelection) {
      onExistingAddressSelection(selectedAddressName);
    }

    // Find the selected address
    const selectedAddress = existingShippingAddress.find((addr: any) => {
      const parsedAddr = parseJSONField(addr);
      return parsedAddr?.name === selectedAddressName;
    });

    if (selectedAddress) {
      const parsedAddress = parseJSONField(selectedAddress);

      form.setValue("street", parsedAddress?.street || "");
      form.setValue("suite", parsedAddress?.suite || "");
      form.setValue("city", parsedAddress?.city || "");
      form.setValue("state", parsedAddress?.state || "");
      form.setValue("zipCode", parsedAddress?.postal_code || "");
      form.setValue("country", parsedAddress?.country || "");

      toast.success(`Address "${selectedAddressName}" selected and form filled`);
    }
  };

  console.log("existingShippingAddress", existingShippingAddress);

  return (
    <Card className="border shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Shipping Information
        </CardTitle>
        <CardDescription>Please enter your shipping details</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="firstName">First Name</Label>
                    <FormControl>
                      <Input id="firstName" placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="lastName">Last Name</Label>
                    <FormControl>
                      <Input id="lastName" placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="email">Email</Label>
                  <FormControl>
                    <Input id="email" type="email" placeholder="john.doe@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {existingShippingAddress && existingShippingAddress.length > 0 && (
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="existingShippingAddress"
                  render={({ field }) => (
                    <FormItem>
                      <Label
                        htmlFor="existingShippingAddress"
                        className="text-sm font-medium flex items-center gap-2"
                      >
                        <Building className="h-4 w-4" />
                        Choose from Saved Addresses
                      </Label>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          handleAddressSelection(value);
                        }}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11 w-full">
                            <SelectValue placeholder="Select a saved address">
                              {field.value &&
                                (() => {
                                  const selectedAddr = existingShippingAddress.find((addr: any) => {
                                    const parsed = parseJSONField(addr);
                                    return parsed?.name === field.value;
                                  });

                                  if (selectedAddr) {
                                    const parsed = parseJSONField(selectedAddr);
                                    return (
                                      <div className="flex items-center gap-2 text-left">
                                        <Building className="h-4 w-4 text-gray-500" />
                                        <span className="font-medium">{parsed?.name}</span>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-w-full">
                          <ScrollArea className="max-h-80">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {existingShippingAddress.map((address: any, index: number) => {
                                const parsedAddress = parseJSONField(address);

                                return (
                                  <SelectItem
                                    key={parsedAddress?.name || index}
                                    value={parsedAddress?.name}
                                    className="cursor-pointer focus-visible:border-none hover:border-none hover:outline-none px-2"
                                  >
                                    <div className="flex flex-col gap-1 py-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <Badge
                                          variant="outline"
                                          className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                                        >
                                          {parsedAddress?.name || "Address"}
                                        </Badge>
                                      </div>

                                      <div className="text-xs text-gray-600 space-y-0.5">
                                        {(parsedAddress?.first_name ||
                                          parsedAddress?.last_name) && (
                                          <div className="flex items-center gap-1">
                                            <User className="h-3 w-3 flex-shrink-0" />
                                            <span className="truncate">
                                              {parsedAddress?.first_name} {parsedAddress?.last_name}
                                            </span>
                                          </div>
                                        )}

                                        {parsedAddress?.email && (
                                          <div className="flex items-center gap-1">
                                            <Mail className="h-3 w-3 flex-shrink-0" />
                                            <span className="truncate">{parsedAddress?.email}</span>
                                          </div>
                                        )}

                                        {parsedAddress?.phone && (
                                          <div className="flex items-center gap-1">
                                            <Phone className="h-3 w-3 flex-shrink-0" />
                                            <span className="truncate">{parsedAddress?.phone}</span>
                                          </div>
                                        )}

                                        <div className="flex items-start gap-1">
                                          <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                          <div className="flex flex-col min-w-0">
                                            <span className="truncate text-gray-800">
                                              {parsedAddress?.street}
                                              {parsedAddress?.suite && `, ${parsedAddress?.suite}`}
                                            </span>
                                            <span className="truncate">
                                              {parsedAddress?.city}, {parsedAddress?.state}{" "}
                                              {parsedAddress?.zip_code}
                                            </span>
                                            <span className="truncate text-gray-500">
                                              {parsedAddress?.country}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <span>💡</span>
                  Select an address above to auto-fill the form, or fill in manually below
                </p>
              </div>
            )}

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="phone">Phone</Label>
                  <FormControl>
                    <Input id="phone" type="text" placeholder="555 456-123" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="street"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="street">Street Address</Label>
                    <FormControl>
                      <Input id="street" placeholder="123 Main Street" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="suite"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="suite">Apartment, Suite, etc. (Optional)</Label>
                    <FormControl>
                      <Input id="suite" placeholder="Apt 4B" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="col-span-2 md:col-span-1">
                    <Label htmlFor="city">City</Label>
                    <FormControl>
                      <Input id="city" placeholder="New York" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="state">State</Label>
                    <FormControl>
                      <Input id="state" placeholder="NY" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="zipCode">Zip Code</Label>
                    <FormControl>
                      <Input id="zipCode" placeholder="10001" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-900">Country</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 w-full">
                        <SelectValue placeholder={field.value || "Select country"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <ScrollArea className="h-44">
                        {countries.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>

        {!form.watch("existingShippingAddress") && (
          <div className="flex items-center gap-3 mt-6 p-4 bg-gray-50 rounded-lg border">
            <Checkbox
              checked={wantToSaveAddress}
              onCheckedChange={handleSaveShippingAddress}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <div>
              <p className="text-sm font-medium">Save shipping address for future use</p>
              <p className="text-xs text-gray-500">Quick checkout for your next order</p>
            </div>
          </div>
        )}

        <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Save Shipping Address
              </DialogTitle>
              <DialogDescription>
                Give this address a memorable name so you can easily find it later.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="addressName" className="text-sm font-medium">
                Address Name
              </Label>
              <Input
                id="addressName"
                placeholder="e.g., Home, Office, Work, Mom's House"
                value={addressName}
                onChange={(e) => setAddressName(e.target.value)}
                className="mt-2"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                Choose a unique name that helps you identify this address
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button onClick={handleAddressNameSave} className="bg-primary hover:bg-primary/90">
                Save Address
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ShippingInformation;
